import React from "react";
import Button from "@material-ui/core/Button";
import colors from "../../constants/colors";
import { useUser } from "../../context/userContext";
import "./SimulationControls.css";
import constants from "../../constants/PathSimulation/pathSimulationConstants";

/**
 * Componente de visualização da fita que mostra o estado atual do autômato
 * posicionado sobre o símbolo a ser lido.
 *
 * @param {Object} props - As propriedades do componente.
 * @param {string} props.word - A palavra de entrada (a fita).
 * @param {number} props.currentIndex - O índice atual de consumo da fita.
 * @param {string} props.stateName - O nome do estado atual do autômato.
 * @param {Array<Object>} props.machineStates - Lista de todos os estados (usada para verificar estado final).
 * @returns {JSX.Element | null} O visualizador da fita.
 */
const TapeVisualizer = ({ word, currentIndex, stateName, machineStates }) => {
  if (!word) {
    return null;
  }

  const wordChars = Array.from(word);
  const consumptionIndex = currentIndex;

  // === CÁLCULOS DE POSICIONAMENTO ===
  const CELL_WIDTH = 20;
  const CELL_PADDING = 8;
  const CELL_BORDER = 1;
  const CELL_GAP = 5;
  const CELL_TOTAL_WIDTH = CELL_WIDTH + 2 * CELL_PADDING + 2 * CELL_BORDER;
  const STEP_WIDTH = CELL_TOTAL_WIDTH + CELL_GAP;

  const stateOffset = consumptionIndex * STEP_WIDTH;

  // Verifica se o estado atual é final
  const isCurrentStateFinal = machineStates.find((s) => s.text === stateName);
  const stateBoxShadow =
    isCurrentStateFinal && isCurrentStateFinal.isFinal
      ? "0 0 0 3px black inset"
      : "none";

  return (
    <div className="tape__visualizer">
      <div className="tape__relative-wrapper">
        {stateName && (
          <div
            className="tape__state-pointer"
            style={{
              marginLeft: `${stateOffset}px`,
              position: "absolute",
              top: 0,
              left: 0,
              transform: "translate(-50%, 0)",
              transition: "margin-left 0.3s ease-in-out",
              zIndex: 10,
            }}
          >
            {/* O PRÓPRIO ESTADO (BOLINHA) */}
            <div
              className="tape__state-circle"
              style={{
                backgroundColor: colors.simulationActiveState,
                boxShadow: stateBoxShadow,
              }}
            >
              {stateName.length > 4 ? stateName.substring(0, 3) : stateName}
            </div>
            {/* SETA APONTANDO PARA O SÍMBOLO */}
            <div className="tape__state-arrow" />
          </div>
        )}

        {/* A FITA DE ENTRADA */}
        <div className="tape__wrapper">
          {wordChars.map((char, index) => {
            let cellModifier = "";
            let cellBgColor = "white";

            if (index < consumptionIndex) {
              cellModifier = "tape__cell--consumed";
              cellBgColor = "#E0FFE0";
            } else if (index === consumptionIndex) {
              cellModifier = "tape__cell--active";
              cellBgColor = colors.simulationActiveState;
            }

            return (
              <div
                key={index}
                className={`tape__cell ${cellModifier}`}
                style={{
                  backgroundColor: cellBgColor,
                  fontWeight: index === consumptionIndex ? "bold" : "normal",
                  boxShadow:
                    index === consumptionIndex
                      ? "0 0 5px rgba(0,0,0,0.3)"
                      : "none",
                }}
              >
                {char === " " ? " " : char}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * Componente de controle de simulação (rodapé).
 * Contém os botões de navegação, status e resultado de aceitação/rejeição.
 *
 * @param {Object} props - As propriedades do componente.
 * @param {Array<string>} props.simulationPath - A lista de estados da trajetória.
 * @param {number} props.simulationIndex - O índice do passo atual.
 * @param {string} props.currentSimulationStateName - O nome do estado atual.
 * @param {Object} props.responseSimulate - O objeto de resposta completa da API.
 * @param {boolean} props.isFinalStep - Indica se é o último passo da simulação.
 * @param {string} props.language - O idioma atual para traduções.
 * @param {Array<Object>} props.machineStates - Lista de todos os estados da máquina.
 * @param {Array<Object>} props.machineTransitions - Lista de todas as transições da máquina.
 * @param {Function} props.setSimulationIndex - Setter do índice de simulação (do useDot).
 * @param {Function} props.setIsSimulating - Setter da flag de simulação (do useDot).
 * @param {Function} props.setSimulationPath - Setter do caminho de simulação (do useDot).
 * @returns {JSX.Element} O painel de controle da simulação.
 */
export default function SimulationControls(props) {
  const { language } = useUser();
  const { setAlert } = props;

  const {
    simulationPath,
    simulationIndex,
    currentSimulationStateName,
    responseSimulate,
    isFinalStep,
    machineStates,
    setSimulationIndex,
    setIsSimulating,
    setSimulationPath,
  } = props;

  // ====================================================================
  // === HANDLERS DE NAVEGAÇÃO ===
  // ====================================================================

  const handleNextStep = () => {
    if (simulationIndex < simulationPath.length - 1) {
      setSimulationIndex(simulationIndex + 1);
    } else {
      let message;

      if (constants.pathSimulation.steps.endOfPath[language]) {
        message = constants.pathSimulation.steps.endOfPath[language];
      } else {
        message = constants.pathSimulation.steps.endOfPath["pt"];
      }

      setAlert({
        active: true,
        text: message,
        color: "info",
        severity: "info",
      });
    }
  };

  const handlePreviousStep = () => {
    if (simulationIndex > 0) {
      setSimulationIndex(simulationIndex - 1);
    }
  };

  const handleStopSimulating = () => {
    setIsSimulating(false);
    setSimulationIndex(0);
    setSimulationPath([]);
  };

  // ====================================================================
  // === LÓGICA DE EXIBIÇÃO DE STATUS ===
  // ====================================================================

  let wordTape = "";
  if (
    responseSimulate &&
    responseSimulate.responseSimulateAutomata &&
    responseSimulate.responseSimulateAutomata.word
  ) {
    wordTape = responseSimulate.responseSimulateAutomata.word;
  }

  let tapeSteps = [];
  if (
    responseSimulate &&
    responseSimulate.responseSimulateAutomata &&
    responseSimulate.responseSimulateAutomata.tape
  ) {
    tapeSteps = responseSimulate.responseSimulateAutomata.tape;
  }

  const consumptionIndex = simulationIndex;

  let currentSymbolRead = "";

  // Passo 0: Início
  if (simulationIndex === 0) {
    currentSymbolRead = ""; // Nenhum símbolo lido ainda
  } else {
    const currentStep = tapeSteps[simulationIndex];
    const previousStep = tapeSteps[simulationIndex - 1];

    if (currentStep && previousStep) {
      const currentPos = currentStep.pos;
      const previousPos = previousStep.pos;

      if (currentPos > previousPos) {
        const consumedTapeIndex = previousPos;

        if (consumedTapeIndex >= 0 && consumedTapeIndex < wordTape.length) {
          currentSymbolRead = wordTape[consumedTapeIndex];

          if (currentSymbolRead === " ") {
            currentSymbolRead = "";
          }
        } else {
          currentSymbolRead = "";
        }
      } else {
        currentSymbolRead = "λ";
      }
    } else {
      currentSymbolRead = "";
    }
  }

  // LÓGICA DE ACEITAÇÃO/REJEIÇÃO
  let isAccept;
  if (responseSimulate && responseSimulate.responseSimulateAutomata) {
    isAccept = responseSimulate.responseSimulateAutomata.isAccept;
  }

  let resultText = null;
  let resultColorModifier = "rejected";
  let resultColor = "red";

  if (isAccept === true) {
    resultText = constants.pathSimulation.status.accepted[language];
    resultColorModifier = "accepted";
    resultColor = "green";
  } else if (isAccept === false) {
    resultText = constants.pathSimulation.status.rejected[language];
    resultColorModifier = "rejected";
    resultColor = "red";
  }

  return (
    <div className="simulation-controls__wrapper">
      {/* VISUALIZAÇÃO DA FITA */}
      <TapeVisualizer
        word={wordTape}
        currentIndex={
          tapeSteps[simulationIndex] &&
          tapeSteps[simulationIndex].pos !== undefined
            ? tapeSteps[simulationIndex].pos
            : consumptionIndex
        }
        stateName={currentSimulationStateName}
        machineStates={machineStates}
      />

      {/* LINHA DE STATUS E RESULTADO (PASSOS + REJEITADA/ACEITA)*/}
      <div className="simulation-controls__status-line">
        {/* Informação de Passo e Símbolo Lido */}
        <span className="simulation-controls__status-item">
          {constants.pathSimulation.steps.step[language]}: {simulationIndex + 1}{" "}
          / {simulationPath.length}
        </span>
        <span className="simulation-controls__status-item">
          {constants.pathSimulation.constants.readSymbol[language]}:
          <span className="simulation-controls__symbol-value">
            {currentSymbolRead}
          </span>
        </span>
        {/* Resultado de ACEITAÇÃO/REJEIÇÃO */}
        {isFinalStep && resultText && (
          <span
            className={`simulation-controls__result-tag simulation-controls__result-tag--${resultColorModifier}`}
            style={{ backgroundColor: resultColor }}
          >
            {constants.pathSimulation.constants.wordLabel[language]}:{" "}
            {resultText}
          </span>
        )}
      </div>

      {/* LINHA DE BOTÕES DE NAVEGAÇÃO*/}
      <div className="simulation-controls__buttons-line">
        {/* BOTÃO VOLTAR  */}
        <Button
          variant="contained"
          color="default"
          onClick={handlePreviousStep}
          disabled={simulationIndex <= 0}
        >
          {constants.pathSimulation.steps.back[language]}
        </Button>

        {/* BOTÃO AVANÇAR */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleNextStep}
          disabled={isFinalStep}
        >
          {constants.pathSimulation.steps.next[language]} &#9654;
        </Button>

        {/* BOTÃO ENCERRAR */}
        <Button variant="outlined" onClick={handleStopSimulating}>
          {constants.pathSimulation.steps.stop[language]}
        </Button>
      </div>
    </div>
  );
}
